import { currentLocale } from '@/i18n/interface';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, FileCheck } from 'lucide-react';
import { toast } from "@/hooks/use-toast";
import { useDojoSettings } from "@/hooks/useDojoSettings";
import { getDisclaimerText } from "@/constants/disclaimerText";

const DisclaimerForm = () => {
  const { t } = useTranslation(['forms', 'common']);
  const { settings } = useDojoSettings();
  const [agreed, setAgreed] = useState(false);
  const [parentalConsent, setParentalConsent] = useState(false);
  const [signature, setSignature] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const disclaimerText = getDisclaimerText(settings.dojo_name, 'student');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!agreed || !signature.trim()) {
      toast({
        title: t('forms:disclaimer.incompleteForm'),
        description: t('forms:disclaimer.incompleteDescription'),
        variant: "destructive"
      });
      return;
    }

    setIsSubmitted(true);
    toast({
      title: t('forms:disclaimer.disclaimerSigned'),
      description: t('forms:disclaimer.disclaimerSignedDescription')
    });
  };

  if (isSubmitted) {
    return (
      <Card className="max-w-2xl mx-auto card-elegant animate-scale-in">
        <CardContent className="p-8 text-center bg-gradient-to-br from-dojo-success-light/20 to-transparent">
          <FileCheck className="h-16 w-16 text-dojo-success mx-auto mb-4 animate-gentle-bounce" />
          <h2 className="text-2xl font-bold text-gradient-primary mb-2">{t('forms:disclaimer.signedSuccessfully')}</h2>
          <p className="text-gray-600 mb-4">
            {t('forms:disclaimer.thankYou', {
              date: new Date(date).toLocaleDateString(currentLocale(), {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })
            })}
          </p>
          <p className="text-sm text-gray-500">
            {t('forms:disclaimer.digitalSignature')} <span className="font-medium">{signature}</span>
          </p>
          {parentalConsent && (
            <p className="text-sm text-blue-600 mt-2">
              {t('forms:disclaimer.parentalConsentConfirmed')}
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in-up">
      <Card className="card-elegant">
        <CardHeader className="bg-gradient-to-r from-dojo-primary-light/20 to-transparent">
          <CardTitle className="flex items-center gap-2 text-gradient-primary">
            <Shield className="h-5 w-5 text-dojo-primary animate-gentle-bounce" />
            {t('forms:disclaimer.title')}
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {t('forms:disclaimer.description')}
          </CardDescription>
        </CardHeader>
      </Card>

      <Card className="card-polished">
        <CardHeader>
          <CardTitle className="text-gradient-secondary">{t('forms:disclaimer.agreementTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-96 w-full rounded-md border border-border-subtle bg-background-subtle/30 p-4 shadow-soft">
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
              {disclaimerText}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      <Card className="card-elegant">
        <CardHeader>
          <CardTitle className="text-gradient-primary">{t('forms:disclaimer.signatureTitle')}</CardTitle>
          <CardDescription className="text-muted-foreground">
            {t('forms:disclaimer.signatureDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="signature">{t('forms:disclaimer.fullNameLabel')}</Label>
                <Input
                  id="signature"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder={t('forms:placeholders.fullLegalName')}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">{t('forms:disclaimer.dateLabel')}</Label>
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-group-polished">
              <Checkbox
                id="agreement"
                checked={agreed}
                onCheckedChange={(checked) => setAgreed(checked as boolean)}
              />
              <div className="space-y-1">
                <Label
                  htmlFor="agreement"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t('forms:disclaimer.agreementCheckbox')}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t('forms:disclaimer.agreementConfirmation')}
                </p>
              </div>
            </div>

            <div className="form-group-polished bg-dojo-accent-light/20 border-dojo-accent/20">
              <Checkbox
                id="parentalConsent"
                checked={parentalConsent}
                onCheckedChange={(checked) => setParentalConsent(checked as boolean)}
              />
              <div className="space-y-1">
                <Label
                  htmlFor="parentalConsent"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-dojo-accent"
                >
                  {t('forms:disclaimer.parentalConsentCheckbox')}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t('forms:disclaimer.parentalConsentDescription')}
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="submit"
                variant="dojo"
                size="lg"
                disabled={!agreed || !signature.trim()}
                className="group"
              >
                <FileCheck className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform duration-300" />
                {t('forms:disclaimer.signAgreement')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default DisclaimerForm;